/** What the dashboard does for the active organization. */
export type DashboardState =
  | 'loading'
  | 'signInUnavailable'
  | 'ready'
  | 'install'
  | 'selectOrganization'
  | 'redirectOnboarding';

/**
 * Where the Clerk session stands on organization membership.
 *
 *  - `pending`  — Clerk has not finished loading the membership list, or a `setActive` is in
 *                 flight. The `setActive` half is bounded by the attempt store's deadline. The
 *                 Clerk-load half is not bounded at all, so a client that never finishes loading
 *                 holds this value indefinitely.
 *  - `none`     — the list loaded and the user belongs to no organization at all.
 *  - `noActive` — the user has memberships but the session has no active organization (the
 *                 auto-select never ran, or its `setActive` was rejected).
 *  - `active`   — an organization is active and its id is in the session token.
 */
export type OrganizationMembership =
  | 'pending'
  | 'unavailable'
  | 'none'
  | 'noActive'
  | 'active';

export interface DashboardStateInput {
  /** Where the Clerk session stands on organization membership. */
  membership: OrganizationMembership;
  /** installationStatus?.status for the active organization ('pending' | 'active' | 'missing'). */
  installStatus: 'pending' | 'active' | 'missing' | undefined;
  /** Whether the active organization has a subscription (useSubscription data != null). */
  hasSubscription: boolean;
}

/**
 * decideDashboardState decides what the dashboard shows:
 *
 *  - `loading`           — membership is still resolving. Every other input is meaningless until
 *                          it settles, so this is checked first.
 *  - `install`           — no active VCS install, OR the user belongs to no organization yet.
 *                          Both cases want the same next step, which lives on github.com and so
 *                          cannot be an auto-redirect.
 *  - `selectOrganization`— the user has organizations but none is active. Terminal and
 *                          actionable; never a spinner.
 *  - `ready`             — organization active, GitHub App installed, subscription: the full dashboard.
 *  - `redirectOnboarding`— install pending (started, not active) OR active install but no
 *                          subscription yet: the next step is to finish onboarding. A
 *                          multi-organization user who didn't mean to set this organization up
 *                          escapes via the organization switcher in the onboarding nav.
 *
 * A zero-membership user gets `install` rather than a redirect to /onboarding on purpose:
 * /onboarding without an `installation_id` renders the payment step and its submit is a no-op,
 * so redirecting there would strand the user behind a form that silently swallows a card. The
 * GitHub App install is the genuine first step — it returns with the `installation_id` that
 * onboarding needs.
 *
 * Membership must be resolved before this is called with a meaningful `installStatus`; the
 * `loading` branch is what enforces that ordering.
 */
export function decideDashboardState({
  membership,
  installStatus,
  hasSubscription,
}: DashboardStateInput): DashboardState {
  if (membership === 'pending') {
    return 'loading';
  }
  if (membership === 'unavailable') {
    return 'signInUnavailable';
  }
  if (membership === 'none') {
    return 'install';
  }
  if (membership === 'noActive') {
    return 'selectOrganization';
  }
  if (installStatus === 'active') {
    return hasSubscription ? 'ready' : 'redirectOnboarding';
  }
  if (installStatus === 'pending') {
    return 'redirectOnboarding';
  }
  // 'missing' (or undefined while a query settles) — nothing installed yet.
  return 'install';
}
